import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  ConditionalTemplateFilter_CreateConditionalTemplateFilterInput,
  ConditionalTemplateFilter_FieldsFragment,
  ConditionalTemplateFilter_GetConditionalTemplateFiltersByQuestionIdDocument,
  ConditionalTemplateFilter_UpdateConditionalTemplateFilterInput,
  ConditionalTemplateQuestion_FieldsFragment,
  ConditionalTemplateQuestion_GetConditionalTemplateQuestionsAvailableForFilteringByFilterQuestionIdDocument,
  useConditionalTemplateAnswer_GetConditionalTemplateAnswersByQuestionIdLazyQuery,
  useConditionalTemplateAnswer_GetConditionalTemplateAnswersByQuestionIdQuery,
  useConditionalTemplateFilter_CreateConditionalTemplateFilterMutation,
  useConditionalTemplateFilter_UpdateConditionalTemplateFilterMutation,
  useConditionalTemplateQuestion_GetConditionalTemplateQuestionsAvailableForFilteringByFilterQuestionIdQuery,
} from 'queries';
import * as yup from 'yup';

import AutocompleteFormSelectField from 'common/autocomplete-form-select-field';
import FormSelectField from 'common/form-select-field';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import ConditionalTemplateFilterMultipleSelectCheckmarks from './conditional-template-filter-multiple-select-checkmarks';

type Props = {
  conditionalTemplateQuestionId: string;
  isEditing: boolean;
  selectedConditionalTemplateFilter?: ConditionalTemplateFilter_FieldsFragment | undefined;
  onExitClick: () => void;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
    '& input': {
      paddingRight: '10px',
      padding: '1vh 2vh',
    },
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    textAlign: 'left',
  },
  submitButton: {
    paddingTop: '10px',
  },
}));

type ConditionalTemplateFilterFormValues = {
  conditionalTemplateQuestionId: string;
  filterQuestionId: string;
  filterAnswerId: string;
  conditionalTemplateFilterToAnswerIds: string[];
};

const schema = yup.object().shape({
  filterQuestionId: yup.string().required('Required'),
  filterAnswerId: yup.string().required('Required'),
  conditionalTemplateFilterToAnswerIds: yup.array(),
});

export default function ConditionalTemplateFilterForm({
  conditionalTemplateQuestionId,
  isEditing,
  selectedConditionalTemplateFilter,
  onExitClick,
}: Props) {
  const classes = useStyles();

  const [filterAnswers, setFilterAnswers] = useState<any[]>([]);
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(false);

  const { data: conditionalTemplateAnswerData, loading: conditionalTemplateAnswerDataLoading } =
    useConditionalTemplateAnswer_GetConditionalTemplateAnswersByQuestionIdQuery({
      fetchPolicy: 'network-only',
      variables: { input: { conditionalTemplateQuestionId } },
    });

  const {
    data: availableConditionalTemplateQuestions,
    loading: availableConditionalTemplateQuestionsLoading,
  } =
    useConditionalTemplateQuestion_GetConditionalTemplateQuestionsAvailableForFilteringByFilterQuestionIdQuery(
      {
        fetchPolicy: 'network-only',
        variables: { input: { conditionalTemplateQuestionId } },
      },
    );

  const [loadFilterAnswers] =
    useConditionalTemplateAnswer_GetConditionalTemplateAnswersByQuestionIdLazyQuery({
      fetchPolicy: 'network-only',
      async onCompleted(data) {
        if (data.data.length > 0) {
          setDisableSaveButton(false);
          setFilterAnswers(data.data);
          return;
        }
        setDisableSaveButton(true);
        showToast({
          message: 'No available question / answer combinations for this filter question',
          severity: NotificationSeverity.INFO,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting all filter answers: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [
    createConditionalTemplateFilter,
    { loading: executingCreateConditionalTemplateFilterMutation },
  ] = useConditionalTemplateFilter_CreateConditionalTemplateFilterMutation({
    refetchQueries: [
      {
        query: ConditionalTemplateFilter_GetConditionalTemplateFiltersByQuestionIdDocument,
        variables: { id: conditionalTemplateQuestionId },
      },
    ],
    onCompleted: () => {
      showToast({
        message: 'Conditional template filter created',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error creating the conditional template filter: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [
    updateConditionalTemplateFilter,
    { loading: executingUpdateConditionalTemplateFilterMutation },
  ] = useConditionalTemplateFilter_UpdateConditionalTemplateFilterMutation({
    refetchQueries: [
      {
        query: ConditionalTemplateFilter_GetConditionalTemplateFiltersByQuestionIdDocument,
        variables: { id: conditionalTemplateQuestionId },
      },
      {
        query:
          ConditionalTemplateQuestion_GetConditionalTemplateQuestionsAvailableForFilteringByFilterQuestionIdDocument,
        variables: { input: { conditionalTemplateQuestionId } },
      },
    ],
    onCompleted: () => {
      showToast({
        message: 'Conditional template filter updated',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating conditional template filter: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
    setValue,
  } = useForm<ConditionalTemplateFilterFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      conditionalTemplateQuestionId,
      filterQuestionId: selectedConditionalTemplateFilter?.filterQuestionId || '',
      filterAnswerId: selectedConditionalTemplateFilter?.filterAnswerId || '',
      conditionalTemplateFilterToAnswerIds:
        selectedConditionalTemplateFilter?.conditionalTemplateFilterToAnswers
          ? selectedConditionalTemplateFilter?.conditionalTemplateFilterToAnswers.map(
              (filterToAnswerJoin) => filterToAnswerJoin.conditionalTemplateAnswer.id,
            )
          : [],
    },
  });

  const submitForm = async ({ ...formData }: ConditionalTemplateFilterFormValues) => {
    if (selectedConditionalTemplateFilter) {
      const input: ConditionalTemplateFilter_UpdateConditionalTemplateFilterInput = {
        conditionalTemplateQuestionId: selectedConditionalTemplateFilter.id,
        conditionalTemplateFilterToAnswerIds: formData.conditionalTemplateFilterToAnswerIds,
      };

      updateConditionalTemplateFilter({
        variables: {
          input,
        },
      });
    } else {
      const input: ConditionalTemplateFilter_CreateConditionalTemplateFilterInput = {
        ...formData,
        conditionalTemplateQuestionId,
      };

      createConditionalTemplateFilter({
        variables: {
          input,
        },
      });
    }
  };

  if (availableConditionalTemplateQuestionsLoading || conditionalTemplateAnswerDataLoading) {
    return <Spinner />;
  }

  const filterQuestions: ConditionalTemplateQuestion_FieldsFragment[] =
    availableConditionalTemplateQuestions?.data || [];

  const filterAvailableQuestions = isEditing
    ? [
        {
          label: selectedConditionalTemplateFilter?.filterQuestion?.question || '',
          value: selectedConditionalTemplateFilter?.filterQuestionId || '',
        },
      ]
    : filterQuestions.map((filterQuestion) => ({
        label: filterQuestion.question,
        value: filterQuestion.id,
      }));

  const filterAnswersSelectOptions = filterAnswers.map((filterAnswer) => ({
    label: filterAnswer.answer,
    value: filterAnswer.id,
  }));

  const selectedConditionalTemplateAnswerSelectOptions =
    conditionalTemplateAnswerData?.data.map((conditionalTemplateAnswerData) => ({
      label: `${
        conditionalTemplateAnswerData.documentGenerationTemplate?.name
          ? `${conditionalTemplateAnswerData.documentGenerationTemplate.name} | ${conditionalTemplateAnswerData.answer}`
          : `${conditionalTemplateAnswerData.answer} `
      }`,
      value: conditionalTemplateAnswerData.id,
    })) || [];

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        {isEditing ? 'Edit' : 'Create'} Conditional Template Filter
      </Typography>
      <form
        id="conditional-template-filter-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="filterQuestionId"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={filterAvailableQuestions}
                    label="Filter question"
                    disabled={isEditing}
                    onChange={(evt) => {
                      const selectedFilterQuestionId: string = (evt.target.value as string) || '';
                      setValue('filterQuestionId', selectedFilterQuestionId);
                      loadFilterAnswers({
                        variables: {
                          input: {
                            conditionalTemplateQuestionId: conditionalTemplateQuestionId,
                            excludeAnswersFromFilterQuestionId: selectedFilterQuestionId,
                          },
                        },
                      });
                    }}
                    errorMessage={errors.filterQuestionId?.message}
                  />
                )}
              />
            </Grid>

            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="filterAnswerId"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <AutocompleteFormSelectField
                    {...field}
                    disabled={isEditing}
                    value={field.value}
                    inputRef={ref}
                    options={filterAnswersSelectOptions}
                    placeholder={selectedConditionalTemplateFilter?.filterAnswer?.answer || ''}
                    handleOnChange={(selection: string) => {
                      setValue('filterAnswerId', selection);
                    }}
                    label="Answer for filter question"
                  />
                )}
              />
            </Grid>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="conditionalTemplateFilterToAnswerIds"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <ConditionalTemplateFilterMultipleSelectCheckmarks
                    inputRef={ref}
                    value={field.value || []}
                    options={selectedConditionalTemplateAnswerSelectOptions}
                    label="Answers for selected question"
                    onSelectChange={(selections: string[]) => {
                      setValue('conditionalTemplateFilterToAnswerIds', selections);
                    }}
                    errorMessage={errors.conditionalTemplateFilterToAnswerIds?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={clsx(classes.container, classes.submitButton)}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Button
                type="submit"
                form="conditional-template-filter-form"
                color="secondary"
                variant="contained"
                disabled={
                  isSubmitting ||
                  disableSaveButton ||
                  executingCreateConditionalTemplateFilterMutation ||
                  executingUpdateConditionalTemplateFilterMutation
                }
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </div>
      </form>
    </>
  );
}
